import { QueryClient } from '@tanstack/react-query'
import { fetchPage } from '@vangst/services/oogst/page'
import useGetJobsReport from '@vangst/services/oogst/registrations/useGetJobsReport'
import { RobotsEnum } from '@vangst/services/oogst/types'
import useViewer from '@vangst/services/oogst/viewer/useViewer'
import Routes from '@vangst/services/routes'
import { setSessionRedirect, StorageKeys } from '@vangst/services/storage'
import { InferGetStaticPropsType } from 'next'
import React, { useCallback, useState } from 'react'
import { MdDescription } from 'react-icons/md'
import Busy from '../../components/feedback/Busy'
import Breadcrumbs from '../../components/navigation/Breadcrumbs'
import ClickyBtn from '../../components/navigation/ClickyBtn'
import ClickyLink from '../../components/navigation/ClickyLink'
import Meta from '../../domains/application/Meta'
import NewsletterForm from '../../domains/application/NewsletterForm'
import AlertDialogSignInOrUp from '../../domains/viewer/feedback/DialogSignInOrUp'

export async function getStaticProps() {
  const queryClient = new QueryClient()
  const rawUrl = __filename.split('pages')[1]?.replace('.js', '')
  const page = await fetchPage(queryClient, { rawUrl })
  const crumbs = [
    { href: Routes.INDEX, text: 'Home' },
    { href: Routes.REPORTS, text: 'Reports' },
    { href: null, text: '2023 Jobs Report' },
  ]

  return {
    props: {
      crumbs,
      page: page?.getPage ? page.getPage : null,
    },
    revalidate: 30,
  }
}

// -------------------------------------

type PropsType = InferGetStaticPropsType<typeof getStaticProps>

export default function JobsReport2023(props: PropsType) {
  const [showDialog, setShowDialog] = useState(false)
  const { crumbs, page } = props
  const { authenticated, authenticating } = useViewer()

  const [scene, setScene] = React.useState<1 | 2 | undefined>()

  React.useEffect(() => {
    authenticated ? setScene(2) : setScene(1)
    if (globalThis.sessionStorage?.getItem(StorageKeys.REDIRECT) == null) {
      setSessionRedirect(Routes.REPORTS_JOBS_2023)
    }
  }, [authenticated])

  const { data, isLoading } = useGetJobsReport(undefined, {
    enabled: authenticated || scene === 2,
  })

  const downloadLink = data?.getReport?.url

  const handleSubmit = useCallback(() => {
    setScene(2)
  }, [])

  return (
    <>
      <Meta
        key="meta"
        canonicalUrl={page?.metaInfo.canonicalUrl}
        crumbs={crumbs}
        description={page?.metaInfo.description}
        publishDate={page?.postedAt}
        robots={page?.metaInfo.robots ?? RobotsEnum.IndexFollow}
        title={page?.metaInfo.title ?? page?.title ?? '2023 Jobs Report'}
      />
      <Breadcrumbs crumbs={crumbs} />
      <article className="contain-xyc mood-orange min-h-screen">
        <div className="contain-xyc mood-white rounded-md border">
          <h1 className="heading-xl">The 2023 Jobs Report</h1>
          <div className="mt-8 grid content-start gap-12 sm:grid-cols-2">
            <div className="flow-y-sm">
              <p className="prose">
                Stay ahead of the game in the fast-paced world of legal cannabis
                with the 2023 Vangst Jobs Report, the most comprehensive and
                up-to-date resource for understanding the cannabis industry's
                workforce.
              </p>
              <p className="prose">
                With over 400,000 full-time equivalent jobs supported by legal
                cannabis in early 2023, the industry is reaching an inflection
                point, and our report dives deep into the trends and insights
                you need to know to succeed.
              </p>

              <h2 className="text-lg">Included in the Guide:</h2>
              <ul className="ml-4 flex list-disc flex-col gap-2">
                <li>
                  Analysis of why jobs declined in 2022 and predictions on
                  <strong> whether job growth will return in 2023</strong>.
                </li>

                <li>
                  Identification of America's top cannabis job markets by state
                  and where job growth potential is waiting to happen.
                </li>

                <li>
                  A deep dive into what constitutes a cannabis job, including an
                  overview of cannabis jobs by sector, and{' '}
                  <strong>where job gains and losses are happening.</strong>
                </li>

                <li>
                  Highlight of the biggest job gains and losses in the past year
                  by state.{' '}
                </li>

                <li>
                  <strong>Mature market analysis,</strong> including
                  California's rough patch in 2022 and New York's steady growth
                  for years to come.
                </li>
              </ul>
              <p className="prose">
                Don't miss out on the essential insights and predictions you
                need to navigate the legal cannabis industry's evolving
                workforce!
              </p>
            </div>
            <div className="grid content-start">
              {(scene == null || authenticating) && (
                <Busy className="m-auto w-72 self-start" />
              )}
              {scene === 1 && !authenticating && !authenticated && (
                <>
                  <p>
                    Tell us a little about yourself to access the full report.
                  </p>
                  <NewsletterForm
                    id="jobs-report"
                    onSuccessCallback={handleSubmit}
                  />
                  <span className="mt-8 text-sm">
                    Already a member of Vangst or interested in joining?{' '}
                    <ClickyLink
                      onClick={() => setShowDialog(true)}
                      className="link-orange"
                    >
                      Sign In or Sign Up
                    </ClickyLink>{' '}
                    to view the PDF.
                  </span>
                  {showDialog && (
                    <AlertDialogSignInOrUp
                      handleClose={() => setShowDialog(false)}
                    />
                  )}
                </>
              )}
              {scene === 2 && (
                <ClickyBtn
                  as="text-icon"
                  className="mood-orange m-auto w-72 self-start"
                  disabled={isLoading}
                  href={downloadLink}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <span className="flex items-center gap-2">
                    <MdDescription />
                    <span>Download Report</span>
                  </span>
                </ClickyBtn>
              )}
            </div>
          </div>
        </div>
      </article>
    </>
  )
}
